import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * design-system/10-Accessibility.md §5 — entering a screen moves focus to its heading.
 *
 * After every navigation `document.activeElement` was `BODY` (R0121). A keyboard user tabbed from the top
 * of the document each time; a screen-reader user was not told the screen had changed at all, because
 * nothing had focus to announce and the reading cursor stayed where it was.
 *
 * §5 specifies the focus **order** within a task — instruction → stimulus → options → feedback → action —
 * and that order has to begin somewhere. The heading is where.
 *
 * **One hook in the shell rather than an effect in fourteen screens.** Every screen already renders exactly
 * one `h1` — the audit checked, and `check-behaviour.sh` would notice if one stopped. So the shell can find
 * it without each screen having to hand over a ref, and a new screen gets this by existing.
 *
 * `tabIndex = -1` makes a heading focusable without putting it in the tab order: focus can be moved there
 * by code, and a user tabbing forward from it goes to the first real control, not back through the title.
 */
export function useFocusOnScreenChange(): void {
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    // Not on the first paint. Nobody navigated — the page was opened — and stealing focus from wherever the
    // browser restored it would be a change the user did not ask for.
    //
    // The test is *which* screen we were on, not *whether* this is the first effect. A boolean „already
    // ran" flag looks equivalent and is not: StrictMode runs every effect twice in development, so the
    // second run sees the flag already lowered and focuses the heading on a page load. Comparing the
    // address cannot be fooled that way — on a first paint it is the same address both times.
    if (previousPathname.current === pathname) return;

    previousPathname.current = pathname;

    // One frame, so the new screen has rendered its heading before we look for it.
    const frame = requestAnimationFrame(() => {
      const heading = document.querySelector('h1');

      if (heading === null) return;

      heading.tabIndex = -1;
      heading.focus({ preventScroll: false });
    });

    return () => cancelAnimationFrame(frame);
  }, [pathname]);
}
